import React, { useState, useEffect } from 'react';
import { api } from '../lib/api';
import { DashboardKPIs, DailyActivityPoint, ReferrerStat, DeviceStat } from '../types';
import { MetricCard } from '../components/MetricCard';
import { Card, CardHeader, CardTitle, CardDescription } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { QRCodeModal } from '../components/QRCodeModal';
import {
  Link as LinkIcon,
  CheckCircle,
  MousePointerClick,
  Calendar,
  ExternalLink,
  QrCode,
  Copy,
  TrendingUp,
  RefreshCw,
  Plus,
} from 'lucide-react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { copyToClipboard, formatDate, formatNumber } from '../lib/utils';
import { useToast } from '../hooks/useToast';

interface DashboardOverviewProps {
  onNavigate: (path: string) => void;
  onOpenCreate: () => void;
}

export const DashboardOverview: React.FC<DashboardOverviewProps> = ({ onNavigate, onOpenCreate }) => {
  const { showToast } = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [kpis, setKpis] = useState<DashboardKPIs | null>(null);
  const [clicksOverTime, setClicksOverTime] = useState<DailyActivityPoint[]>([]);
  const [topLinks, setTopLinks] = useState<any[]>([]);
  const [referrers, setReferrers] = useState<ReferrerStat[]>([]);
  const [devices, setDevices] = useState<DeviceStat[]>([]);
  const [selectedDays, setSelectedDays] = useState<number>(30);
  const [qrModalData, setQrModalData] = useState<{ shortCode: string; title: string } | null>(null);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const data = await api.get<{
        kpis: DashboardKPIs;
        clicksOverTime: DailyActivityPoint[];
        topLinks: any[];
        referrers: ReferrerStat[];
        devices: DeviceStat[];
      }>(`/analytics/overview?days=${selectedDays}`);

      setKpis(data.kpis);
      setClicksOverTime(data.clicksOverTime || []);
      setTopLinks(data.topLinks || []);
      setReferrers(data.referrers || []);
      setDevices(data.devices || []);
    } catch (err: any) {
      showToast('Error loading analytics', err.message, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [selectedDays]);

  const handleCopyLink = async (shortCode: string) => {
    const url = `${window.location.origin}/${shortCode}`;
    const ok = await copyToClipboard(url);
    if (ok) {
      showToast('Copied to clipboard', url, 'success');
    }
  };

  return (
    <div className="space-y-8 animate-in fade-in duration-300">
      {/* Top Banner */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-extrabold text-white tracking-tight">
            Marketing Overview
          </h1>
          <p className="text-xs text-slate-400 mt-1">
            Real-time redirect metrics and campaign performance across all channels.
          </p>
        </div>

        <div className="flex items-center gap-2.5">
          <Button
            variant="outline"
            size="sm"
            onClick={fetchData}
            isLoading={isLoading}
            leftIcon={<RefreshCw className="w-3.5 h-3.5" />}
          >
            Refresh
          </Button>

          <Button
            variant="primary"
            size="sm"
            onClick={onOpenCreate}
            leftIcon={<Plus className="w-3.5 h-3.5" />}
          >
            Create New Link
          </Button>
        </div>
      </div>

      {/* KPI Cards Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3.5">
        <MetricCard
          title="Total Links"
          value={kpis?.totalLinks ?? 0}
          icon={LinkIcon}
          accentColor="orange"
        />
        <MetricCard
          title="Active Links"
          value={kpis?.activeLinks ?? 0}
          icon={CheckCircle}
          accentColor="emerald"
        />
        <MetricCard
          title="Total Clicks"
          value={kpis?.totalClicks ?? 0}
          icon={MousePointerClick}
          accentColor="sky"
        />
        <MetricCard
          title="Clicks Today"
          value={kpis?.clicksToday ?? 0}
          icon={TrendingUp}
          accentColor="amber"
        />
        <MetricCard
          title="This Week"
          value={kpis?.clicksThisWeek ?? 0}
          subtitle="Last 7 days"
          icon={Calendar}
          accentColor="indigo"
        />
        <MetricCard
          title="This Month"
          value={kpis?.clicksThisMonth ?? 0}
          subtitle="Last 30 days"
          icon={Calendar}
          accentColor="orange"
        />
      </div>

      {/* Chart Section */}
      <Card>
        <CardHeader>
          <div>
            <CardTitle>Click Activity Over Time</CardTitle>
            <CardDescription>
              Comparing estimated human visitors vs social preview crawlers (Reporting timezone: Asia/Kolkata)
            </CardDescription>
          </div>
          <div className="flex items-center gap-1.5 p-1 bg-slate-900 rounded-xl border border-slate-800 text-xs">
            {[7, 14, 30, 90].map((d) => (
              <button
                key={d}
                onClick={() => setSelectedDays(d)}
                className={`px-2.5 py-1 rounded-lg font-medium transition-colors cursor-pointer ${
                  selectedDays === d ? 'bg-orange-600 text-white' : 'text-slate-400 hover:text-slate-200'
                }`}
              >
                {d}d
              </button>
            ))}
          </div>
        </CardHeader>

        <div className="h-72 w-full pt-4">
          {clicksOverTime.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={clicksOverTime} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="humanColor" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#f97316" stopOpacity={0.4} />
                    <stop offset="95%" stopColor="#f97316" stopOpacity={0.0} />
                  </linearGradient>
                  <linearGradient id="crawlerColor" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#38bdf8" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#38bdf8" stopOpacity={0.0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                <XAxis
                  dataKey="date"
                  stroke="#64748b"
                  fontSize={11}
                  tickFormatter={(val) => val.slice(5)}
                />
                <YAxis stroke="#64748b" fontSize={11} allowDecimals={false} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#0f172a',
                    borderColor: '#334155',
                    borderRadius: '12px',
                    fontSize: '12px',
                  }}
                  itemStyle={{ color: '#f1f5f9' }}
                />
                <Area
                  type="monotone"
                  dataKey="human_clicks"
                  name="Human Clicks"
                  stroke="#f97316"
                  strokeWidth={2}
                  fillOpacity={1}
                  fill="url(#humanColor)"
                />
                <Area
                  type="monotone"
                  dataKey="crawler_requests"
                  name="Crawler Previews"
                  stroke="#38bdf8"
                  strokeWidth={2}
                  fillOpacity={1}
                  fill="url(#crawlerColor)"
                />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-full flex items-center justify-center text-slate-500 text-xs">
              No click records available for this date period yet. Share links to begin tracking!
            </div>
          )}
        </div>
      </Card>

      {/* Grid: Top Links & Traffic Sources */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Top Links Table (2 cols) */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader>
              <div>
                <CardTitle>Top Performing Links</CardTitle>
                <CardDescription>Highest volume short links across campaigns</CardDescription>
              </div>
              <Button variant="ghost" size="sm" onClick={() => onNavigate('/admin/links')}>
                View All Links
              </Button>
            </CardHeader>

            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead>
                  <tr className="border-b border-slate-800 text-slate-400 font-semibold uppercase tracking-wider">
                    <th className="pb-3 px-2">Short Link</th>
                    <th className="pb-3 px-2">Destination</th>
                    <th className="pb-3 px-2 text-right">Clicks</th>
                    <th className="pb-3 px-2 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800/60">
                  {topLinks.length > 0 ? (
                    topLinks.map((link) => (
                      <tr key={link.id} className="hover:bg-slate-900/40 transition-colors">
                        <td className="py-3 px-2">
                          <div className="flex items-center gap-2">
                            <span className="font-mono font-bold text-orange-400">/{link.short_code}</span>
                            <button
                              onClick={() => handleCopyLink(link.short_code)}
                              className="text-slate-500 hover:text-slate-300 transition-colors cursor-pointer"
                              title="Copy short URL"
                            >
                              <Copy className="w-3.5 h-3.5" />
                            </button>
                          </div>
                          <p className="text-[11px] text-slate-300 font-medium truncate max-w-[200px]">
                            {link.title}
                          </p>
                        </td>
                        <td className="py-3 px-2">
                          <a
                            href={link.destination_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-slate-400 hover:text-slate-200 flex items-center gap-1 font-mono truncate max-w-[220px]"
                          >
                            <span className="truncate">{link.destination_url}</span>
                            <ExternalLink className="w-3 h-3 shrink-0" />
                          </a>
                        </td>
                        <td className="py-3 px-2 text-right font-mono font-bold text-slate-100">
                          {formatNumber(link.total_clicks)}
                        </td>
                        <td className="py-3 px-2 text-right">
                          <button
                            onClick={() =>
                              setQrModalData({
                                shortCode: link.short_code,
                                title: link.title,
                              })
                            }
                            className="p-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 transition-colors cursor-pointer"
                            title="Generate QR code"
                          >
                            <QrCode className="w-3.5 h-3.5" />
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={4} className="py-8 text-center text-slate-500">
                        No links created yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </div>

        {/* Traffic Sources Breakdown (1 col) */}
        <div>
          <Card>
            <CardHeader>
              <div>
                <CardTitle>Top Referrers</CardTitle>
                <CardDescription>Primary traffic acquisition channels</CardDescription>
              </div>
            </CardHeader>

            <div className="space-y-4 pt-1">
              {referrers.length > 0 ? (
                referrers.map((ref) => (
                  <div key={ref.source} className="space-y-1.5">
                    <div className="flex items-center justify-between text-xs">
                      <span className="font-medium text-slate-200">{ref.source}</span>
                      <span className="font-mono text-slate-400">
                        {ref.clicks} clicks ({ref.percentage}%)
                      </span>
                    </div>
                    <div className="w-full h-2 rounded-full bg-slate-800 overflow-hidden">
                      <div
                        className="h-full rounded-full bg-orange-500"
                        style={{ width: `${ref.percentage}%` }}
                      />
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-xs text-slate-500 text-center py-6">
                  No referrer logs recorded yet.
                </p>
              )}
            </div>
          </Card>
        </div>
      </div>

      {/* QR Code Modal */}
      {qrModalData && (
        <QRCodeModal
          isOpen={!!qrModalData}
          onClose={() => setQrModalData(null)}
          shortCode={qrModalData.shortCode}
          linkTitle={qrModalData.title}
        />
      )}
    </div>
  );
};
